/* eslint-disable dot-notation */
import React, { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { push } from 'connected-react-router';

import NotificationsList from '@components/header/notifications-list';

import * as colors from '@constants/colors';
import { getEntitiesListConfig, getEntityTypeLabel } from '@constants/config';
import { topBarStyles } from '@constants/mui-theme';

import LogoutIcon from '@icons/header/logout-icon';
import SettingsIcon from '@icons/header/settings-icon';
import EntityTypeIcon from '@icons/entity-type-icon';

import { withStyles } from 'tss-react/mui';
import NotificationsIcon from '@mui/icons-material/Notifications';

import { Badge, Icon, IconButton, Popover, Toolbar } from '@mui';

import IconMenu from '@shared/icon-menu';
import IconMenuItem from '@shared/icon-menu-item';

import { getUserAvatar } from '@utils/data-table/user';
import { canAddEntities, isReadOnly } from '@utils/permission-utils';
import { openHelpPage } from '@utils/shared-utils';

import styles from './action-items.scss';
import UnstyledHeading from '@components/shared/unstyled-heading';

const USER_ACTION_HEADER_ID = 'user-actions-header';

const CustomBadge = withStyles(Badge, {
  root: {
    color: topBarStyles.icons.color,
    '& .MuiBadge-badge': {
      backgroundColor: colors.dotmapsBadgeRed
    }
  }
});

const ActionItems = () => {
  const dispatch = useDispatch();

  // Redux state
  const user = useSelector(state => state.config.user);
  const notificationsCount = useSelector(state => state.notifications.unread);
  const permissions = useSelector(state => state.config.permissions);

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [userOpen, setUserOpen] = useState(false);
  const [userAnchorEl, setUserAnchorEl] = useState(null);

  const openNotifications = event => {
    setNotificationsOpen(true);
    setNotificationsAnchorEl(event.currentTarget);
  };

  const closeNotifications = () => setNotificationsOpen(false);

  const openUser = event => {
    setUserOpen(true);
    setUserAnchorEl(event.currentTarget);
  };

  const closeUser = () => setUserOpen(false);

  const goTo = pathname => {
    const source = window.location.pathname;
    closeUser();
    dispatch(push({ pathname, state: { source } }));
  };

  const goToSettings = () => goTo('/settings/profile');

  const goToLogout = () => goTo('/logout');

  const goToAdd = dataType => goTo(`/${dataType}/new`);

  const entities = [];
  getEntitiesListConfig().forEach(entity => {
    if (canAddEntities(null, entity, 'add') && !isReadOnly(entity, permissions)) {
      entities.push(entity);
    }
  });

  if (!user) {
    return null;
  }

  const userAvatarIcon = getUserAvatar(user, 'large');
  return (
    <Toolbar style={topBarStyles.toolbar} role="navigation" aria-labelledby={USER_ACTION_HEADER_ID}>
      <UnstyledHeading rank={2} id={USER_ACTION_HEADER_ID} ariaOnly>User Actions</UnstyledHeading>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={openNotifications} aria-label="Notifications">
          <CustomBadge
            badgeContent={notificationsCount}
            color="secondary"
            max={9}
            overlap="rectangular"
          >
            <NotificationsIcon />
          </CustomBadge>
        </IconButton>
        <Popover
          open={notificationsOpen}
          anchorEl={notificationsAnchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={closeNotifications}
          classes={{paper: styles['notifications-list-container']}}
        >
          <NotificationsList visible={notificationsOpen} />
        </Popover>
        <IconButton onClick={openHelpPage} aria-label="help">
          <Icon color={topBarStyles.icons.color} aria-hidden="true">help</Icon>
        </IconButton>
        {entities.length > 0 &&
          <IconMenu
            icon={<Icon color={colors.overlayTextColor} aria-hidden="true">add</Icon>}
            iconButtonStyles={topBarStyles.addIcon}
            MenuProps={{
              anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
              transformOrigin: { horizontal: 'center', vertical: 'top' }
            }}
            tooltip="Add"
          >
            {entities.map(entity => (
              <IconMenuItem
                key={entity}
                icon={<EntityTypeIcon type={entity} size="1.5rem" />}
                onClick={() => goToAdd(entity)}
              >
                New {getEntityTypeLabel(entity)}
              </IconMenuItem>
            ))}
          </IconMenu>
        }
        <IconButton onClick={openUser} aria-label="User menu" style={{ padding: 0, margin: '1rem 0 1rem 1rem' }}>
          {getUserAvatar(user, 'medium')}
        </IconButton>
        <Popover
          open={userOpen}
          anchorEl={userAnchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={closeUser}
        >
          <div className={styles['action-items-container']}>
            <div className={styles['user-card']}>
              <div className={styles.avatar}>{userAvatarIcon}</div>
              <div className={styles.content}>
                <div className={styles.name}>{user.name}</div>
                <div className={styles.email}>{user.email}</div>
                <div>{user.permission}</div>
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.list} >
              <div
                className={styles['list-item']}
                onClick={goToSettings}
                role="button"
                tabIndex={0}
                onKeyDown={event => event.key === 'Enter' && goToSettings()}
              >
                <div className={styles.icon}>
                  <SettingsIcon />
                </div>
                <div className={styles['link']}>Settings</div>
              </div>
              <div
                className={styles['list-item']}
                onClick={goToLogout}
                role="button"
                tabIndex={0}
                onKeyDown={event => event.key === 'Enter' && goToLogout()}
              >
                <div className={styles.icon}>
                  <LogoutIcon />
                </div>
                <div className={styles['link']}>Logout</div>
              </div>
            </div>
          </div>
        </Popover>
      </div>
    </Toolbar>
  );
};

export default ActionItems;
